import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import * as qlqueries from '../graphql/queries';
import * as customQueries from '../graphql/customQueries';
import * as routerQueries from '../graphql/routerQueries';

const client = generateClient();

const parseDataJson = (dataJson) => {
  if (!dataJson) return null;
  try {
    return JSON.parse(dataJson);
  } catch (error) {
    console.error('Failed to parse dataJson:', error, dataJson);
    return null;
  }
};

const isCurrentEventLive = (event, now = moment.utc()) => {
  if (event?.status === 'LIVE') return true;
  if (!event?.serviceStartTime || !event?.serviceEndTime) return false;

  const serviceStart = moment.utc(event.serviceStartTime);
  const serviceEnd = moment.utc(event.serviceEndTime);

  if (!serviceStart.isValid() || !serviceEnd.isValid()) return false;

  return now.isBetween(serviceStart, serviceEnd, undefined, '[]');
};

const state = {
  inputs: [],
  inputInfos: {},
  outputs: [],
  outputInfos: {},
  liveInputIds: [],
  liveOutputIds: [],
  loading: false,
  loadingStatuses: false,
  loadingLiveAssignments: false,
};

const getters = {
  inputsByType: (state) => (type) => {
    if (!type) return state.inputs;
    return state.inputs.filter((input) => input.type === type);
  },

  availableTypes: (state) => {
    const types = new Set(state.inputs.map((input) => input.type));
    return Array.from(types).sort();
  },

  getInputInfo: (state) => (inputId) => state.inputInfos[inputId] || null,

  isInputHealthy: (state) => (inputId) => {
    const info = state.inputInfos[inputId];
    if (!info) return null;
    // MEDIA_CONNECT format
    if (info.Messages && info.Messages.length > 0) {
      return !info.Messages.some(
        (msg) => msg.Code === 'StreamError' || msg.Code === 'Error' || msg.Code === 'Critical',
      );
    }
    // ROUTE format - no error mechanism, healthy if status exists
    if (info.status) return true;
    // MEDIA_CONNECT with no messages
    if (info.State) return true;
    return null;
  },

  getInputState: (state) => (inputId) => {
    const info = state.inputInfos[inputId];
    if (!info) return null;
    // MEDIA_CONNECT: State (PascalCase)
    if (info.State) return info.State;
    // ROUTE: status.status (camelCase)
    if (info.status?.status) return info.status.status.toUpperCase();
    return null;
  },

  // Output getters
  outputsByType: (state) => (type) => {
    if (!type) return state.outputs;
    return state.outputs.filter((output) => output.outputType === type);
  },

  availableOutputTypes: (state) => {
    const types = new Set(state.outputs.map((output) => output.outputType));
    return Array.from(types).sort();
  },

  getOutputInfo: (state) => (outputId) => state.outputInfos[outputId] || null,

  isOutputHealthy: (state) => (outputId) => {
    const info = state.outputInfos[outputId];
    if (!info) return null;
    if (info.Messages && info.Messages.length > 0) {
      return !info.Messages.some(
        (msg) => msg.Code === 'StreamError' || msg.Code === 'Error' || msg.Code === 'Critical',
      );
    }
    if (info.State) return true;
    return null;
  },

  getOutputState: (state) => (outputId) => {
    const info = state.outputInfos[outputId];
    if (!info) return null;
    if (info.State) return info.State;
    return null;
  },

  liveInputIds: (state) => state.liveInputIds,

  liveOutputIds: (state) => state.liveOutputIds,
};

const mutations = {
  setInputs: (state, payload) => {
    state.inputs = payload.items || [];
  },

  setInputInfos: (state, payload) => {
    state.inputInfos = { ...state.inputInfos, ...payload };
  },

  setOutputs: (state, payload) => {
    state.outputs = payload.items || [];
  },

  setOutputInfos: (state, payload) => {
    state.outputInfos = { ...state.outputInfos, ...payload };
  },

  setLiveAssignments: (state, { inputIds, outputIds }) => {
    state.liveInputIds = inputIds || [];
    state.liveOutputIds = outputIds || [];
  },

  setLoading: (state, payload) => {
    state.loading = payload;
  },

  setLoadingStatuses: (state, payload) => {
    state.loadingStatuses = payload;
  },

  setLoadingLiveAssignments: (state, payload) => {
    state.loadingLiveAssignments = payload;
  },
};

const actions = {
  async fetchInputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listInputsRouterAlt,
        variables: { limit: 1500 },
      });
      const { data } = result;
      commit('setInputs', data.listInputs);
      return data.listInputs;
    } catch (error) {
      console.error('SourceMonitoring fetchInputs error', error);

      const hasData = !!error.data && !!error.data.listInputs;
      if (hasData) {
        const { data } = error;
        commit('setInputs', data.listInputs);
        this._vm.$message.error({
          message: "Oops something went wrong. Can't get some of the inputs",
        });
        return { error: true, message: error.errors };
      }

      this._vm.$message.error({
        message: "Oops something went wrong. Can't get inputs",
      });
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchInputInfos({ commit, state }, inputIds) {
    const ids = inputIds || state.inputs.map((input) => input.id);
    if (ids.length === 0) return;

    commit('setLoadingStatuses', true);
    try {
      const result = await client.graphql({
        query: routerQueries.routerInputsTargetInfo,
        variables: { inputIds: ids },
      });

      const response = result?.data?.routerInputsTargetInfo;
      if (response && response.success) {
        const parsed = parseDataJson(response.dataJson);
        if (parsed) {
          const infosMap = {};
          if (Array.isArray(parsed)) {
            parsed.forEach((info) => {
              if (info.id) {
                infosMap[info.id] = info;
              }
            });
          } else if (typeof parsed === 'object') {
            Object.assign(infosMap, parsed);
          }
          commit('setInputInfos', infosMap);
        }
      }
    } catch (error) {
      console.error('SourceMonitoring fetchInputInfos error', error);
      this._vm.$message.error({
        message: "Oops something went wrong. Can't get input statuses",
      });
    } finally {
      commit('setLoadingStatuses', false);
    }
  },

  async fetchOutputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listOutputsRouterAlt,
        variables: { limit: 1500 },
      });
      const { data } = result;
      commit('setOutputs', data.listOutputs);
      return data.listOutputs;
    } catch (error) {
      console.error('SourceMonitoring fetchOutputs error', error);

      const hasData = !!error.data && !!error.data.listOutputs;
      if (hasData) {
        const { data } = error;
        commit('setOutputs', data.listOutputs);
        this._vm.$message.error({
          message: "Oops something went wrong. Can't get some of the outputs",
        });
        return { error: true, message: error.errors };
      }

      this._vm.$message.error({
        message: "Oops something went wrong. Can't get outputs",
      });
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchOutputInfos({ commit, state }, outputIds) {
    const ids = outputIds || state.outputs
      .filter((output) => output.outputType === 'MEDIA_CONNECT')
      .map((output) => output.id);
    if (ids.length === 0) return;

    commit('setLoadingStatuses', true);
    try {
      const result = await client.graphql({
        query: routerQueries.routerOutputsTargetInfo,
        variables: { outputIds: ids },
      });

      const response = result?.data?.routerOutputsTargetInfo;
      if (response && response.success) {
        const parsed = parseDataJson(response.dataJson);
        if (parsed) {
          const infosMap = {};
          if (Array.isArray(parsed)) {
            parsed.forEach((info) => {
              if (info.id) {
                infosMap[info.id] = info;
              }
            });
          } else if (typeof parsed === 'object') {
            Object.assign(infosMap, parsed);
          }
          commit('setOutputInfos', infosMap);
        }
      }
    } catch (error) {
      console.error('SourceMonitoring fetchOutputInfos error', error);
      this._vm.$message.error({
        message: "Oops something went wrong. Can't get output statuses",
      });
    } finally {
      commit('setLoadingStatuses', false);
    }
  },

  async fetchLiveAssignments({ commit }) {
    commit('setLoadingLiveAssignments', true);

    try {
      const liveInputIds = new Set();
      const liveOutputIds = new Set();
      const now = moment.utc();
      const startTime = now.clone().subtract(4, 'days').toISOString();
      const endTime = now.clone().add(4, 'days').toISOString();

      const fetchPage = async (nextToken = null) => {
        const result = await client.graphql({
          query: customQueries.sourceMonitoringEventsByStartTime,
          variables: {
            startTime,
            endTime,
            limit: 1000,
            nextToken,
          },
        });

        const { data } = result;
        const events = data?.eventsByStartTime?.items || [];
        const newNextToken = data?.eventsByStartTime?.nextToken || null;

        events.filter(Boolean).forEach((event) => {
          if (!isCurrentEventLive(event, now)) {
            return;
          }

          if (event.input?.id) {
            liveInputIds.add(event.input.id);
          }

          if (event.inputOverride?.id) {
            liveInputIds.add(event.inputOverride.id);
          }

          event.outputs?.items?.filter(Boolean).forEach((outputMap) => {
            if (outputMap.output?.id) {
              liveOutputIds.add(outputMap.output.id);
            }
          });
        });

        if (newNextToken) {
          await fetchPage(newNextToken);
        }
      };

      await fetchPage();

      commit('setLiveAssignments', {
        inputIds: Array.from(liveInputIds),
        outputIds: Array.from(liveOutputIds),
      });
    } catch (error) {
      console.error('SourceMonitoring fetchLiveAssignments error', error);
      this._vm.$message.error({
        message: "Oops something went wrong. Can't get live assignments",
      });
    } finally {
      commit('setLoadingLiveAssignments', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
